import {
  createShapeId,
  Editor,
  TLShape,
  TLShapeId,
  TLTextShapeProps,
  toRichText,
} from "tldraw";

import { isPageRegionChild } from "@/components/canvas-editor/bindings/layout";
import {
  buildTree,
  compareShapesByCanvasPosition,
} from "@/components/canvas-editor/build-manifest";
import {
  AuditLog,
  Audit,
  auditVerdict,
} from "@/components/canvas-editor/shapes/audit";
import {
  customNodeType,
  CustomShapes,
  FeedShape,
  isCustomNodeShape,
  isEachShape,
  isLogicShape,
  LayoutBinding,
} from "@/components/canvas-editor/shapes/shapes";

let id = 0;

// Whether this shape's TYPE supports `resultState` -- deliberately NOT whether
// this particular instance already carries the key.
//
// `resultState` is an optional prop and no util defaults it, so tldraw builds a
// new shape's props as `{...getDefaultProps(), ...partial}` with no such key at
// all. The writes in this file are the only things that ever add it, so testing
// the instance self-locks: a shape that has never been coloured can never BE
// coloured, and nothing in the app can recover it. Custom nodes take the worst
// of that because they are the one shape we delete and re-create in normal use
// (a version update rebuilds the shape from `mergeNodeVersion`, and expanded
// previews are rebuilt by `buildBindings`), so each update silently drops the
// key again.
//
// The guard is still needed -- `clearAuditLog` sweeps every page shape,
// including notes, text and arrows whose validators reject an unknown prop --
// so ask the util what the type declares instead of what the record happens to
// hold.
// `undecided` has no colour of its own -- see the note at the call site.
function canvasResultState(audit: Audit | undefined) {
  const verdict = auditVerdict(audit);
  return verdict === "undecided" ? "idle" : verdict;
}

function supportsResultState(editor: Editor, shape: TLShape) {
  const props = (
    editor.getShapeUtil(shape).constructor as {
      props?: Record<string, unknown>;
    }
  ).props;

  return props !== undefined && "resultState" in props;
}

// The canvas mirrors the feed's saved tldraw snapshot; the audit tree mirrors the
// saved manifest, which is what Debug actually evaluates. Those are two
// serialisations of the same feed that are meant to agree, and in ~4% of live
// feeds they do not -- feed 34020's canvas root has 7 children where its
// manifest root has 8, because a save wrote a new manifest while carrying the
// previous snapshot forward unchanged.
//
// Pairing children by position means one extra or missing child shifts every
// sibling after it, so those nodes get coloured from their neighbours' results:
// a confident wrong answer. What follows pairs them by operator instead, and
// colours a node only from the audit node it can actually be paired with.
//
// `undefined` is an opaque key: it pairs with anything.
type ConditionKey = string | undefined;

function filterKey(condition: unknown): ConditionKey {
  if (!condition || typeof condition !== "object") {
    return undefined;
  }

  // Underscore-prefixed keys are engine annotations sitting BESIDE the operator
  // (`_explain_error`), not operators. Key order is whatever the serialiser produced, so
  // without this the operator name can come back as `_explain_error` and the node pairs with
  // no shape at all -- landing it in the "left uncoloured" drift message, which would point
  // the reader at canvas drift for what is actually an engine error on that leaf.
  return Object.keys(condition).find(
    (key) => key !== "metadata" && !key.startsWith("_"),
  );
}

function shapeConditionKey(editor: Editor, shape: TLShape): ConditionKey {
  if (isLogicShape(shape)) {
    return shape.props.type;
  }

  if (isEachShape(shape)) {
    return "each";
  }

  // A custom node's stored template is expanded on the way into the manifest,
  // but `expandEaches` only ever rewrites the CHILDREN of an and/or and returns
  // anything else untouched, so the template's own top-level key is exactly what
  // the manifest node carries. Worth keying properly rather than treating the
  // node as opaque: on feed 34020 an opaque node greedily pairs with the extra
  // manifest child and drags both custom nodes onto the wrong results.
  if (isCustomNodeShape(shape)) {
    return filterKey(shape.props.filter);
  }

  try {
    // Cheap: the container types above are already handled, so what reaches here
    // is a leaf and `buildTree` does not recurse.
    return filterKey(buildTree(editor, shape));
  } catch {
    return undefined;
  }
}

function keysPair(a: ConditionKey, b: ConditionKey) {
  return a === undefined || b === undefined || a === b;
}

function sequencesAgree(canvas: ConditionKey[], audit: ConditionKey[]) {
  return (
    canvas.length === audit.length &&
    canvas.every((key, i) => keysPair(key, audit[i]))
  );
}

// Longest common subsequence over two key sequences. Returns, per left-hand
// entry, the right-hand entry it pairs with, or `undefined` for no counterpart.
function lcsPairs(
  canvasKeys: ConditionKey[],
  auditKeys: ConditionKey[],
): (number | undefined)[] {
  const width = auditKeys.length + 1;
  const table = new Array<number>((canvasKeys.length + 1) * width).fill(0);
  const at = (i: number, j: number) => table[i * width + j] ?? 0;

  for (let i = canvasKeys.length - 1; i >= 0; i--) {
    for (let j = auditKeys.length - 1; j >= 0; j--) {
      table[i * width + j] = keysPair(canvasKeys[i], auditKeys[j])
        ? at(i + 1, j + 1) + 1
        : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }

  const pairing = new Array<number | undefined>(canvasKeys.length).fill(
    undefined,
  );
  let i = 0;
  let j = 0;

  while (i < canvasKeys.length && j < auditKeys.length) {
    if (keysPair(canvasKeys[i], auditKeys[j])) {
      pairing[i] = j;
      i++;
      j++;
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      i++;
    } else {
      j++;
    }
  }

  return pairing;
}

// Pairing runs in two passes, because they answer different questions.
//
// A key that occurs exactly ONCE on each side is a FORCED pair: no other
// assignment is possible, whatever order the two sides are in. That is what
// recovers a permutation, which LCS cannot do at all -- LCS pairs may never
// cross, and feed 8189's root is a straight rotation (canvas
// [or, and, regex_matches] against manifest [regex_matches, or, and]), while
// feeds 35159 and 35190 have a plain swap ([regex_any, and] vs [and, regex_any]).
// Those come from a snapshot whose child order no longer matches the manifest's,
// which no amount of re-sorting on our side can recover.
//
// Whatever is left over goes to LCS, which is the right tool for the insertions
// and deletions that ordering cannot explain (feed 34012: canvas 6 children,
// manifest 8).
//
// An opaque key never takes part in the forced pass: it pairs with anything, so
// "occurs once" tells us nothing about it.
function alignChildren(
  canvasKeys: ConditionKey[],
  auditKeys: ConditionKey[],
): (number | undefined)[] {
  const occurrences = (keys: ConditionKey[]) => {
    const counts = new Map<string, number>();

    for (const key of keys) {
      if (key === undefined) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return counts;
  };

  const canvasCounts = occurrences(canvasKeys);
  const auditCounts = occurrences(auditKeys);
  const pairing = new Array<number | undefined>(canvasKeys.length).fill(
    undefined,
  );
  const paired = new Set<number>();

  canvasKeys.forEach((key, i) => {
    if (key === undefined) return;
    if (canvasCounts.get(key) !== 1 || auditCounts.get(key) !== 1) return;

    const j = auditKeys.indexOf(key);

    if (j === -1) return;

    pairing[i] = j;
    paired.add(j);
  });

  const canvasLeft = canvasKeys
    .map((_key, i) => i)
    .filter((i) => pairing[i] === undefined);
  const auditLeft = auditKeys
    .map((_key, j) => j)
    .filter((j) => !paired.has(j));

  lcsPairs(
    canvasLeft.map((i) => canvasKeys[i]),
    auditLeft.map((j) => auditKeys[j]),
  ).forEach((auditSlot, canvasSlot) => {
    if (auditSlot === undefined) return;

    const i = canvasLeft[canvasSlot];
    const j = auditLeft[auditSlot];

    if (i === undefined || j === undefined) return;

    pairing[i] = j;
  });

  return pairing;
}

function annotateNode(
  editor: Editor,
  feed: FeedShape,
  shape: CustomShapes,
  audit: Audit | undefined,
  unpaired: { count: number },
) {
  if (supportsResultState(editor, shape)) {
    editor.updateShape({
      ...shape,
      props: {
        ...shape.props,
        // An undecided node is left UNCOLOURED rather than given a colour of its own.
        //
        // A fourth `resultState` would be the nicer UI, and it is not worth what it costs
        // here: `resultState` is a validated tldraw shape prop declared in 34 shape files,
        // and the whole editor snapshot — shape props included — is persisted into the feed
        // by `save-state.ts`. So a feed saved during a Debug run would carry the new literal,
        // and any client still on an older bundle would fail validation loading it. That is
        // exactly how the strict `metadataType` leaf took the canvas down twice (ui #120).
        //
        // Uncoloured says "no verdict", which is true. Red says "would be hidden", which is
        // not. The actual explanation goes to the post card and the run message, which are
        // plain React and cost nothing.
        resultState: canvasResultState(audit),
      },
    });
  }

  if ((shape.type as string) === customNodeType) {
    const [previewBinding] = editor.getBindingsFromShape<LayoutBinding>(
      shape,
      "layout",
    );

    if (previewBinding) {
      const previewShape = editor.getShape(previewBinding.toId);

      if (previewShape && supportsResultState(editor, previewShape)) {
        shape = previewShape as CustomShapes;
        editor.updateShape({
          ...shape,
          props: {
            ...shape.props,
            resultState: canvasResultState(audit),
          },
        });
      }
    }
  }

  if (audit?.scores.length && typeof audit.scores[0] === "number") {
    const widthDiff = (feed.props.w - shape.props.w) / 2;

    editor.createShape({
      type: "text",
      id: createShapeId(`debug-annotation-${id++}`),
      x: feed.props.w - widthDiff + 20,
      y: (shape.props.h - 20) / 2,
      parentId: shape.id,
      meta: {
        isAnnotation: true,
      },
      props: {
        richText: toRichText(`${audit.scores[0]}`),
        font: "mono",
      } satisfies Partial<TLTextShapeProps>,
    });
  }

  const bindings = editor.getBindingsFromShape<LayoutBinding>(shape, "layout");

  if (!bindings.length) {
    return;
  }

  // Order children by their position on the canvas, exactly as `buildTree` does
  // -- NOT by layout binding index, which is what this used to do. The manifest
  // Debug evaluates was written in position order, so that is the order the
  // audit tree comes back in. The two orders agree only while the layout is in
  // sync with the indices, and on 3 of 4,704 live feeds they had diverged: feed
  // 5006's index order puts an `and` group last where its position order puts it
  // third, which is where its manifest has it -- so at that level EVERY child
  // was paired with a neighbour's result.
  // 🔴 TIE-BROKEN BY BINDING INDEX, or re-running Debug can paint a different
  // answer from the same audit.
  //
  // `compareShapesByCanvasPosition` is `bY - aY` and nothing else, so every
  // shape sharing a y compares equal and the order falls through to whatever
  // order `getBindingsFromShape` happened to return. Author-drawn nodes are
  // never exactly level, so this was invisible — until a custom node is
  // EXPANDED. `updatePreview` deletes the old preview, calls `buildBindings` to
  // create the new one, and calls `setDebug` on the very next line, before any
  // layout pass has moved the fresh shapes off their shared starting point. So
  // the whole expanded body is tied, and its pairing against `sub_audit` is
  // decided by store order.
  //
  // Reported 2026-09-22: "re-running debug can cause different results with no
  // change and i think it's related to whether the custom node is expanded".
  // It is: collapsed there are no children to mis-pair, expanded there are.
  //
  // The binding index is the order `buildBindings` created them in, which for a
  // generated preview IS the manifest order, so it is the right answer for the
  // exact case that is tied. Deliberately applied HERE and not inside
  // `compareShapesByCanvasPosition`: that comparator also orders children when
  // the canvas is compiled into a manifest, and giving tied shapes a new order
  // there could rewrite a saved feed's manifest on its next save. This is
  // annotation only — it changes what is painted, never what is stored.
  const indexOfShape = new Map(
    bindings.map((binding) => [binding.toId, binding.props.index]),
  );
  const byPosition = compareShapesByCanvasPosition(editor);
  const childShapes = bindings
    .map((binding) => editor.getShape(binding.toId))
    .filter((child): child is TLShape => Boolean(child))
    .sort((a, b) => {
      const positional = byPosition(a, b);
      if (positional !== 0) return positional;
      const aIndex = indexOfShape.get(a.id);
      const bIndex = indexOfShape.get(b.id);
      if (aIndex === undefined || bIndex === undefined) return 0;
      return aIndex < bIndex ? -1 : aIndex > bIndex ? 1 : 0;
    });
  const subAudits = audit?.sub_audit ?? [];

  // Position is right whenever the two trees agree, which is the overwhelming
  // majority of nodes, and staying on it keeps those feeds byte-identical. Only
  // reach for alignment once they demonstrably disagree.
  const canvasKeys = childShapes.map((child) =>
    shapeConditionKey(editor, child),
  );
  const auditKeys = subAudits.map((subAudit) => filterKey(subAudit?.condition));
  // An audit node with no sub-audit at all is not a pairing failure, it is a
  // subtree the evaluator reported no detail for: `evaluate_with_audit` handles
  // only and/or/leaf, so an `each` is audited as a leaf and its canvas child has
  // nothing to pair with (90 live feeds). Those children stay uncoloured exactly
  // as before, and are not reported as drift.
  const pairing =
    audit && subAudits.length > 0 && !sequencesAgree(canvasKeys, auditKeys)
      ? alignChildren(canvasKeys, auditKeys)
      : undefined;

  for (let i = 0; i < childShapes.length; i++) {
    const toShape = childShapes[i];

    if (!toShape) {
      continue;
    }

    const auditIndex = pairing ? pairing[i] : i;

    if (pairing && auditIndex === undefined) {
      unpaired.count++;
    }

    annotateNode(
      editor,
      feed,
      toShape as CustomShapes,
      auditIndex === undefined ? undefined : subAudits[auditIndex],
      unpaired,
    );
  }
}

/** Returns how many canvas nodes had no counterpart in the audit tree and were
 * therefore left uncoloured -- 0 for a canvas that agrees with the saved rules. */
export function annotateFeed(
  editor: Editor,
  feed: FeedShape,
  auditLog: AuditLog,
): number {
  // The rules root is the first NON-layer child; the Page section's layers are
  // never evaluated by Debug and must not be paired with audit nodes.
  const rootNodeBinding = editor
    .getBindingsFromShape<LayoutBinding>(feed, "layout")
    .find((b) => {
      const s = editor.getShape(b.toId);
      return s && !isPageRegionChild(s);
    });

  if (!rootNodeBinding) {
    return 0;
  }

  const toShape = editor.getShape(rootNodeBinding.toId);

  if (!toShape) {
    return 0;
  }

  const unpaired = { count: 0 };
  annotateNode(editor, feed, toShape as CustomShapes, auditLog.audit, unpaired);

  return unpaired.count;
}

function clearAuditLogRecursive(editor: Editor, shape: CustomShapes) {
  if (supportsResultState(editor, shape)) {
    editor.updateShape({
      ...shape,
      props: {
        ...shape.props,
        resultState: "idle",
      },
    });
  }

  const bindings = editor.getBindingsFromShape<LayoutBinding>(shape, "layout");

  if (!bindings.length) {
    return;
  }

  for (let i = 0; i < bindings.length; i++) {
    const binding = bindings[i];

    if (!binding) {
      continue;
    }

    const toShape = editor.getShape(binding.toId);

    if (!toShape) {
      continue;
    }

    clearAuditLogRecursive(editor, toShape as CustomShapes);
  }
}

export function clearAuditLog(editor: Editor) {
  const currentPage = editor.getCurrentPageId();
  const shapes = Array.from(editor.getPageShapeIds(currentPage))
    .map((id) => editor.getShape(id))
    .filter((shape) => shape?.parentId.startsWith("page:"));

  shapes.forEach((shape) => {
    clearAuditLogRecursive(editor, shape as CustomShapes);
  });

  const annotations = Array.from(editor.getPageShapeIds(currentPage))
    .map((id) => editor.getShape(id))
    .filter((shape) => shape?.meta?.isAnnotation);

  annotations.forEach((annotation) => {
    editor.deleteShape(annotation?.id as TLShapeId);
  });
}
